<section class="case-study-detail-hero">
  <div class="container">
    <div class="row">
      <div class="col-lg-12">
        <div class="hero-content text-center">
          <span class="hero-badge">Healthcare Case Study</span>
          <h1 class="hero-title">AI-Powered Diagnostics Platform</h1>
          <p class="hero-description">Deployed NVIDIA H100 GPU cluster for real-time medical image analysis, reducing diagnosis time by 75%.</p>
        </div>
      </div>
    </div>
  </div>
</section>

<section class="case-study-overview ptb-100">
  <div class="container">
    <div class="row">
      <div class="col-lg-8">
        <div class="overview-content">
          <h2>Overview</h2>
          <p>MediTech Solutions Pvt. Ltd., a leading healthcare technology company, was facing significant challenges with their medical imaging diagnostics process. Traditional methods of analyzing medical images such as X-rays, MRIs, and CT scans were time-consuming and required specialized radiologists, leading to delays in patient diagnosis and treatment.</p>
          
          <h3>The Challenge</h3>
          <ul class="challenge-list">
            <li>Manual analysis of medical images taking 30-45 minutes per case</li>
            <li>Shortage of qualified radiologists in rural areas</li>
            <li>Increasing patient load and decreasing turnaround time requirements</li>
            <li>Need for consistent and accurate diagnosis across all locations</li>
          </ul>
          
          <h3>Our Solution</h3>
          <p>Insightful IT Services implemented a cutting-edge AI-powered diagnostics platform using NVIDIA H100 GPUs to automate the analysis of medical images. The solution included:</p>
          
          <div class="solution-features">
            <div class="feature-item">
              <i class="bx bx-chip"></i>
              <div class="feature-content">
                <h4>High-Performance GPU Cluster</h4>
                <p>Deployed a cluster of NVIDIA H100 GPUs for parallel processing of medical images</p>
              </div>
            </div>
            
            <div class="feature-item">
              <i class="bx bx-data"></i>
              <div class="feature-content">
                <h4>Deep Learning Models</h4>
                <p>Trained specialized neural networks for different types of medical imaging</p>
              </div>
            </div>
            
            <div class="feature-item">
              <i class="bx bx-cloud"></i>
              <div class="feature-content">
                <h4>Cloud Infrastructure</h4>
                <p>Scalable cloud architecture to handle variable workloads</p>
              </div>
            </div>
          </div>
          
          <h3>Implementation Process</h3>
          <div class="timeline">
            <div class="timeline-item">
              <div class="timeline-date">Week 1-2</div>
              <div class="timeline-content">
                <h4>Requirements Analysis</h4>
                <p>Comprehensive assessment of MediTech's existing infrastructure and diagnostic workflows</p>
              </div>
            </div>
            
            <div class="timeline-item">
              <div class="timeline-date">Week 3-6</div>
              <div class="timeline-content">
                <h4>Infrastructure Setup</h4>
                <p>Deployment of NVIDIA H100 GPU cluster with high-speed storage and networking</p>
              </div>
            </div>
            
            <div class="timeline-item">
              <div class="timeline-date">Week 7-10</div>
              <div class="timeline-content">
                <h4>Model Training</h4>
                <p>Training of AI models using historical medical imaging data</p>
              </div>
            </div>
            
            <div class="timeline-item">
              <div class="timeline-date">Week 11-12</div>
              <div class="timeline-content">
                <h4>Integration & Testing</h4>
                <p>Integration with existing hospital systems and comprehensive testing</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div class="col-lg-4">
        <div class="client-info-card">
          <div class="client-header">
            <img src="assets/img/case-studies/healthcare.jpg" alt="MediTech Solutions" class="client-logo">
            <h3>MediTech Solutions Pvt. Ltd.</h3>
            <p class="industry">Healthcare Technology</p>
          </div>
          
          <div class="client-details">
            <div class="detail-item">
              <span class="label">Location:</span>
              <span class="value">Bangalore, India</span>
            </div>
            
            <div class="detail-item">
              <span class="label">Industry:</span>
              <span class="value">Healthcare</span>
            </div>
            
            <div class="detail-item">
              <span class="label">Employees:</span>
              <span class="value">1,200+</span>
            </div>
            
            <div class="detail-item">
              <span class="label">Project Duration:</span>
              <span class="value">3 Months</span>
            </div>
          </div>
          
          <div class="results-summary">
            <h4>Key Results</h4>
            <div class="result-item">
              <span class="result-value">75%</span>
              <span class="result-label">Faster Diagnosis</span>
            </div>
            
            <div class="result-item">
              <span class="result-value">40%</span>
              <span class="result-label">Cost Reduction</span>
            </div>
            
            <div class="result-item">
              <span class="result-value">99.2%</span>
              <span class="result-label">Accuracy Rate</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</section>

<section class="technology-section bg-light ptb-100">
  <div class="container">
    <div class="section-title text-center mb-5">
      <h2>Technology Stack</h2>
      <p>Advanced technologies that powered this successful implementation</p>
    </div>
    
    <div class="row">
      <div class="col-lg-4 col-md-6 mb-4">
        <div class="tech-card">
          <div class="tech-icon">
            <i class="bx bx-chip"></i>
          </div>
          <h3>NVIDIA H100 GPUs</h3>
          <p>High-performance computing for real-time image processing and AI inference</p>
        </div>
      </div>
      
      <div class="col-lg-4 col-md-6 mb-4">
        <div class="tech-card">
          <div class="tech-icon">
            <i class="bx bx-code"></i>
          </div>
          <h3>Python & TensorFlow</h3>
          <p>Machine learning framework for developing and training diagnostic models</p>
        </div>
      </div>
      
      <div class="col-lg-4 col-md-6 mb-4">
        <div class="tech-card">
          <div class="tech-icon">
            <i class="bx bx-cloud"></i>
          </div>
          <h3>Cloud Infrastructure</h3>
          <p>Scalable cloud environment with auto-scaling capabilities</p>
        </div>
      </div>
    </div>
  </div>
</section>

<section class="cta-section ptb-100">
  <div class="container">
    <div class="cta-content-wrapper text-center">
      <h2>Ready to Transform Your Business?</h2>
      <p>See how our solutions can help you achieve similar results</p>
      <div class="cta-actions">
        <a class="btn-cta-primary" routerLink="/contact">
          <i class="bx bx-phone"></i>
          <span>Get In Touch</span>
        </a>
        <a class="btn-cta-secondary" routerLink="/case-studies">
          <i class="bx bx-book"></i>
          <span>View All Case Studies</span>
        </a>
      </div>
    </div>
  </div>
</section>